import {
  useState, useEffect, useContext, FC,
} from 'react';
import {
  Typography,
  Row,
  Col,
  Button,
  message,
  Modal,
  Space,
  Image,
  Table,
  Input,
  InputNumber,
  Icons,
  Form,
} from 'taxaroo-ui';
import { CUSTOMER_IO_EVENTS, trackCustomerIoEvent } from '~src/components/helpers/customer.io';
import { JobContext } from '~src/components/providers/job-provider';
import { TaxPayerContext } from '~src/components/providers/taxpayer-provider';
import { BusinessInformationEntity, PaymentsEntity } from '~src/graphql';
import { useCreatePaymentsMutation, useUpdatePaymentWithInvoicesMutation } from '~src/graphql/mutations/payments';
import { GetPaymentsByTaxYearInterviewIdDocument } from '~src/graphql/queries/payments';
import { useGetBusinessInformationQuery, useGetCustomLayoutsQuery } from '~src/graphql/queries/settings';
import { useAppSelector } from '~src/redux/hooks';

const { PlusOutlined, DeleteOutlined } = Icons;
const { Paragraph, Text, Title } = Typography;

interface CreateOrUpdateInvoiceModalProps {
  isModalVisible: boolean;
  setIsModalVisible: (b: boolean) => void;
  paymentData: PaymentsEntity;
}

const faultTolerantImage = 'data:image/png;base64,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';

const CreateOrUpdateInvoiceModal:FC<CreateOrUpdateInvoiceModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  paymentData,
}) => {
  const isEditing = !!paymentData;
  const [form] = Form.useForm();
  const [companyInformationData, setCompanyInformationData] = useState<any[]>([]);
  const [keys, setKeys] = useState([(Math.random() + 1).toString(36).substring(7)]);
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [prices, setPrices] = useState<number[]>([]);
  const [quantities, setQuantities] = useState<number[]>([]);
  const [itemTotals, setItemTotals] = useState<number[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  // Get user information from Redux store
  const { firmAccountId } = useAppSelector((state) => state.session);
  const { entityId } = useContext(TaxPayerContext);
  const {
    clientFirstName,
    clientLastName,
    taxYearInterviewId,
  } = useContext(JobContext);

  // graphql
  // get logo
  const { data: customLayoutData } = useGetCustomLayoutsQuery();
  const customLayout = customLayoutData?.CustomLayout;

  // Get firm account info
  const { data: firmAccountInfoData } = useGetBusinessInformationQuery();

  const resetFormValues = () => {
    setKeys([(Math.random() + 1).toString(36).substring(7)]);
    setDescriptions(['']);
    setPrices([0]);
    setQuantities([1]);
    setItemTotals([0]);
    form.setFieldsValue({
      'description-0': '',
      'priceEach-0': 0,
      'quantity-0': 1,
      'itemTotal-0': 0,
    });
  };

  useEffect(() => {
    if (paymentData) {
      setKeys(paymentData.Invoices.map((i) => i.id));
      setDescriptions(paymentData.Invoices.map((i) => i.description));
      setPrices(paymentData.Invoices.map((i) => i.priceEach));
      setQuantities(paymentData.Invoices.map((i) => i.quantity));
      setItemTotals(paymentData.Invoices.map((i) => i.quantity * i.priceEach));
      const valuesArray = paymentData.Invoices.map((i, index) => ({
        [`description-${index}`]: i.description,
        [`priceEach-${index}`]: i.priceEach,
        [`quantity-${index}`]: i.quantity,
        [`itemTotal-${index}`]: i.priceEach * i.quantity,
      }));
      const fieldsValue = valuesArray.reduce((obj, item) => ({ ...obj, ...item }), {});
      form.setFieldsValue(fieldsValue);
    } else {
      resetFormValues();
    }
  }, [paymentData, isModalVisible]);

  // Mutation for creating a payment
  const [createPayment, { loading: loadingCreatePayment }] = useCreatePaymentsMutation({
    onCompleted: () => {
      message.success('Payment created successfully');
      setIsModalVisible(false);
      resetFormValues();
    },
    onError: (error) => {
      if (error.message.indexOf('No valid payment method types for this Payment Intent.') === 0
        || error.message === 'Not handled error') {
        message.error('Stripe account is not connected. It can be connected in Setup Invoicing section.');
      } else {
        message.error(error.message);
      }
    },
    refetchQueries: [GetPaymentsByTaxYearInterviewIdDocument],
    awaitRefetchQueries: true,
  });

  // update payments
  const [updatePaymentWithInvoices, { loading: loadingUpdatePayment }] = useUpdatePaymentWithInvoicesMutation({
    onCompleted: () => {
      message.success('Payment updated successfully');
      setIsModalVisible(false);
      resetFormValues();
    },
    onError: (error) => {
      message.error(error.message);
    },
    refetchQueries: [GetPaymentsByTaxYearInterviewIdDocument],
    awaitRefetchQueries: true,
  });

  const deleteInvoiceRow = async (key: any) => {
    const index = keys.indexOf(key);
    // descriptions
    const newDescriptions = [...descriptions];
    newDescriptions.splice(index, 1);
    setDescriptions(newDescriptions);
    const mappedDescriptions = newDescriptions.map((item, i) => ({ [`description-${i}`]: item }));
    const descriptionsObject = Object.assign({}, ...mappedDescriptions);
    // prices
    const newPrices = [...prices];
    newPrices.splice(index, 1);
    setPrices(newPrices);
    const mappedPrices = newPrices.map((item, i) => ({ [`priceEach-${i}`]: item }));
    const pricesObject = Object.assign({}, ...mappedPrices);
    // quantities
    const newQuantities = [...quantities];
    newQuantities.splice(index, 1);
    setQuantities(newQuantities);
    const mappedQuantities = newQuantities.map((item, i) => ({ [`quantity-${i}`]: item }));
    const quantitiesObject = Object.assign({}, ...mappedQuantities);
    // totals
    const newTotals = [...itemTotals];
    newTotals.splice(index, 1);
    setItemTotals(newTotals);
    const mappedTotals = newTotals.map((item, i) => ({ [`itemTotal-${i}`]: item }));
    const totalsObject = Object.assign({}, ...mappedTotals);
    // form
    form.setFieldsValue({
      ...descriptionsObject,
      ...pricesObject,
      ...quantitiesObject,
      ...totalsObject,
    });

    setKeys((oldKeys) => oldKeys.filter((k, keyIndex) => keyIndex !== index));
  };

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, row, index: number) => (
        <Form.Item
          name={`description-${index}`}
          style={{ margin: 0 }}
        >
          <Input placeholder="Enter a description for this line item" value={descriptions[index]} />
        </Form.Item>
      ),
    },
    {
      title: 'Price Each',
      dataIndex: 'priceEach',
      key: 'priceEach',
      render: (text: string, row, index: number) => (
        <Form.Item
          name={`priceEach-${index}`}
          style={{ margin: 0 }}
          initialValue={0}
        >
          <InputNumber
            step="0.01"
            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            value={prices[index]}
            min={0}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text: string, row, index: number) => (
        <Form.Item
          name={`quantity-${index}`}
          style={{ margin: 0 }}
          initialValue={1}
        >
          <InputNumber min={1} max={99} value={quantities[index]} />
        </Form.Item>
      ),
    },
    {
      title: 'Item Total',
      dataIndex: 'itemTotal',
      key: 'itemTotal',
      render: (text: string, row, index: number) => (
        <Form.Item
          name={`itemTotal-${index}`}
          style={{ margin: 0 }}
        >
          <span>
            {itemTotals[index] ? `$ ${itemTotals[index].toFixed(2)}` : '$ 0.00'}
            {/* {invoiceDetails[index]?.itemTotal ? `$ ${invoiceDetails[index]?.itemTotal.toFixed(2)}` : '$ 0.00'} */}
          </span>
        </Form.Item>
      ),
    },
    {
      title: 'Delete',
      dataIndex: 'delete',
      key: 'delete',
      render: (val: any, row: any) => (
        <DeleteOutlined
          onClick={() => { deleteInvoiceRow(row); }}
          style={{ cursor: 'pointer' }}
        />
      ),
    },
  ];

  const generateCompanyInformationData = (firmAccount: BusinessInformationEntity) => {
    const firmAddress = firmAccount?.Address?.[0]?.lineOne
      ? `${firmAccount?.Address?.[0]?.lineOne}, 
        ${firmAccount?.Address?.[0]?.lineTwo}, 
        ${firmAccount?.Address?.[0]?.city}, 
        ${firmAccount?.Address?.[0]?.state}, 
        ${firmAccount?.Address?.[0]?.zip}`
      : null;

    const data = [];
    if (firmAccount?.FirmAccount?.name) {
      data.push({ title: 'Firm Name', value: firmAccount?.FirmAccount?.name });
    }
    if (firmAddress) {
      data.push({ title: 'Firm Address', value: firmAddress });
    }
    if (firmAccount?.Phones?.[0]?.value) {
      data.push({ title: 'Firm Phone', value: firmAccount?.Phones?.[0]?.value });
    }
    setCompanyInformationData(data);
  };

  useEffect(() => {
    if (firmAccountInfoData) {
      const firmAccount = firmAccountInfoData?.BusinessInformation;
      generateCompanyInformationData(firmAccount as BusinessInformationEntity);
    }
  }, [firmAccountInfoData]);

  const cancelButtonOnClick = () => {
    setIsModalVisible(false);
  };

  const addLineItemOnClick = () => {
    setKeys((oldArray) => [...oldArray, (Math.random() + 1).toString(36).substring(7)]);
    setDescriptions((oldArray) => [...oldArray, '']);
    setPrices((oldArray) => [...oldArray, 0]);
    setQuantities((oldArray) => [...oldArray, 0]);
    form.setFieldsValue({
      [`description-${keys.length}`]: '',
      [`priceEach-${keys.length}`]: 0,
      [`quantity-${keys.length}`]: 1,
    });
    setSubmitDisabled(true);
  };

  const onFinish = (values: any) => {
    // format into invoces
    const invoices = [];
    for (let i = 0; i < keys.length; i += 1) {
      const invoice = {
        id: isEditing ? keys[i] : undefined,
        description: values[`description-${i}`],
        priceEach: values[`priceEach-${i}`],
        quantity: values[`quantity-${i}`],
      };
      invoices.push(invoice);
    }

    // check amount before payment
    const totalAmount = invoices.reduce((partialAmount, x) => partialAmount + x.priceEach * x.quantity, 0);
    if (totalAmount <= 1) {
      message.error('Amount is not valid. Please enter total amount > $1');
      return;
    }

    if (isEditing) {
      const invoices2Create = invoices.filter((i) => !paymentData.Invoices.some((invoice) => invoice.id === i.id));
      const invoices2Update = invoices.filter((i) => paymentData.Invoices.some((invoice) => invoice.id === i.id));
      const invoices2Delete = paymentData.Invoices.filter((i) => !invoices.some((invoice) => invoice.id === i.id));
      // console.log('Update payment', { invoices2Create, invoices2Update, invoices2Delete });

      updatePaymentWithInvoices({
        variables: {
          updatePaymentsInput: {
            paymentId: paymentData.id,
            invoices2Create: invoices2Create.map((invoice) => ({
              ...invoice,
              id: undefined,
              __typename: undefined,
              paymentId: undefined,
              createAt: undefined,
              updateAt: undefined,
            })),
            invoices2Update: invoices2Update.map((invoice) => ({
              ...invoice,
              __typename: undefined,
              paymentId: undefined,
              createAt: undefined,
              updateAt: undefined,
            })),
            invoices2Delete: invoices2Delete.map((invoice) => ({
              ...invoice,
              __typename: undefined,
              paymentId: undefined,
              createAt: undefined,
              updateAt: undefined,
            })),
          },
        },
      });

      trackCustomerIoEvent(CUSTOMER_IO_EVENTS.CLICK, {
        Title: 'Update invoice request',
        Source: 'Button click',
        url: window.location.href,
      });
    } else {
      // create the payment
      createPayment({
        variables: {
          createPaymentsInput: {
            firmAccountId,
            taxYearInterviewId,
            entityId,
            invoices,
          },
        },
      });

      trackCustomerIoEvent(CUSTOMER_IO_EVENTS.CLICK, {
        Title: 'Create invoice request',
        Source: 'Button click',
        url: window.location.href,
      });
    }
  }; // end onFinish

  const onValuesChange = (changedValues: any, allValues: any) => {
    const index = Object.keys(changedValues)[0].split('-')[1];

    setDescriptions((oldArray) => {
      const newArray = [...oldArray];
      newArray[index] = allValues[`description-${index}`];
      return newArray;
    });
    setPrices((oldArray) => {
      const newArray = [...oldArray];
      newArray[index] = allValues[`priceEach-${index}`];
      return newArray;
    });
    setQuantities((oldArray) => {
      const newArray = [...oldArray];
      newArray[index] = allValues[`quantity-${index}`];
      return newArray;
    });
    setItemTotals((oldArray) => {
      const newArray = [...oldArray];
      newArray[index] = allValues[`priceEach-${index}`] * allValues[`quantity-${index}`];
      return newArray;
    });

    form.setFieldsValue({
      [`description-${index}`]: allValues[`description-${index}`],
      [`priceEach-${index}`]: allValues[`priceEach-${index}`],
      [`quantity-${index}`]: allValues[`quantity-${index}`],
      [`itemTotal-${index}`]: allValues[`priceEach-${index}`] * allValues[`quantity-${index}`],
    });
  };

  useEffect(() => {
    const isValid = descriptions.every((x) => !!x)
      && prices.every((x) => !!x)
      && quantities.every((x) => !!x)
      && descriptions.length > 0
      && prices.length > 0
      && quantities.length > 0;

    setSubmitDisabled(!isValid);
  }, [descriptions, prices, quantities]);

  const modalTitle = (userName: string) => (
    <>
      <Row>
        <Col span={20}>
          <Title level={4}>{isEditing ? 'Update Invoice' : 'Create Invoice'}</Title>
        </Col>
        <Col span={3}>
          <Image
            style={{ position: 'absolute', top: '-1em' }}
            width={95}
            src={customLayout?.logo}
            fallback={faultTolerantImage}
          />
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Space direction="horizontal">
            <Text type="secondary">{userName}</Text>
          </Space>
        </Col>
      </Row>
    </>
  );

  return (
    <Modal
      bodyStyle={{ maxHeight: 450, overflowY: 'scroll' }}
      title={modalTitle(`${clientFirstName} ${clientLastName}`)}
      okText={isEditing ? 'Update Invoice' : 'Send Invoice'}
      okButtonProps={{ disabled: loadingCreatePayment || submitDisabled, loading: loadingCreatePayment || loadingUpdatePayment }}
      centered
      visible={isModalVisible}
      onOk={form.submit}
      onCancel={cancelButtonOnClick}
      width={1000}
    >
      <Row>
        <Col span={24}>
          {Boolean(companyInformationData.length) && (
            <Row>
              <Paragraph strong style={{ textTransform: 'capitalize' }}>
                {companyInformationData.map((el) => el.value).join(', ')}
              </Paragraph>
            </Row>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form
            onFinish={onFinish}
            form={form}
            onValuesChange={onValuesChange}
            style={{ width: '100%' }}
          >
            <Table
              rowKey={(record) => record}
              columns={columns}
              dataSource={[...keys]}
              // eslint-disable-next-line react/no-unstable-nested-components
              footer={() => (
                <Button
                  type="primary"
                  onClick={addLineItemOnClick}
                  icon={<PlusOutlined />}
                >
                  Add Line Item
                </Button>
              )}
              pagination={false}
            />
          </Form>
        </Col>
      </Row>

    </Modal>
  );
};

export default CreateOrUpdateInvoiceModal;
